import * as React from 'react';
import { List, Card, Divider, ListSubheader, IconButton } from '@mui/material';
import { ActionRow } from '../OverviewCard/ActionRow';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { Data, isDataLoaded } from '../../types/data-providers';
import { DataIndicators } from '../DataIndicators';
import { AppRoute } from '../../routes';
import _ from 'lodash';
import { TimelineListItem } from '../TimelineListItem';
import { getTime } from 'date-fns';
import { dataFromDataset } from '../../data/DataProviders/DataProviderCache';
import { TimelineCardEvent } from '../TimelineListItem/types';
import { createTimelineCurrentData } from '../../lib/signalr-helper';
import { Icon } from '../../lib/icons';
import { useSelector } from 'react-redux';
import { Selectors } from '../../selectors';
import { useSignalRSiteData } from '../../data/DataProviders/SignalRSiteDataProvider';

export const SiteTimelineCard: React.FC<{
    timelineData: Array<
        Data<{
            timeline: TimelineCardEvent[];
            paging: { offset: string | null; limit: string | null; dateTime: string | null; moreRecords: boolean };
        }>
    >;
    selectedSite: string;
    selectedLocation: string;
    testId?: string;
    viewAllEventsAppRoute: AppRoute;
    eventDetailAppRoute: AppRoute;
    style?: React.CSSProperties;
    showExportPopup: Function;
}> = (props) => {
    const { t } = useLanguageLocale();
    const realTimeSiteData = useSignalRSiteData();
    const exportLoading = useSelector(Selectors.TimelineExport.timelineExportLoading);
    const firstChunk = dataFromDataset(props.timelineData);

    const updateTimeline = (newTimeline: TimelineCardEvent[]): void => {
        if (isDataLoaded(firstChunk) && newTimeline.length > 0) {
            _.filter(firstChunk.value.timeline, (timeline) => {
                _.filter(newTimeline, (data) => {
                    if (timeline.alarmId === data.alarmId) timeline.status = data.status;
                    else {
                        if (firstChunk.value.timeline.every((arr) => arr.alarmId !== data.alarmId))
                            firstChunk.value.timeline.push(data);
                    }
                });
            });
        }
    };
    React.useEffect(() => {
        const newTimeline: TimelineCardEvent[] = [];
        _.forEach(realTimeSiteData && realTimeSiteData.alarmStatus, (alarm) => {
            const obj = createTimelineCurrentData(alarm);
            const notExist = newTimeline.every((arr) => arr.alarmId !== obj.alarmId);
            if (notExist) newTimeline.push(obj);
        });

        updateTimeline(newTimeline);
    }, [realTimeSiteData, firstChunk]); // eslint-disable-line react-hooks/exhaustive-deps

    const visibleEvents = isDataLoaded(firstChunk)
        ? _.take(
              _.orderBy(firstChunk.value.timeline, (event) => getTime(new Date(event.dateTime)), 'desc'),
              5
          )
        : [];

    const isEmpty = isDataLoaded(firstChunk) && firstChunk.value.timeline.length === 0;

    return (
        <div data-testid={props.testId} style={{ ...props.style }} >
            <Card>
                <List
                    subheader={
                        <>
                            <ListSubheader color="primary" component="div" style={{ fontWeight: 600 }}>
                                {t('DASHBOARD.SITE_DETAILS.TIMELINE_CARD.TITLE')}
                                <IconButton
                                    data-testid="siteSummary-download"
                                    disabled={exportLoading || isEmpty}
                                    style={{ float: 'right' }}
                                    onClick={(): void => props.showExportPopup(true)}
                                    size="large">
                                    <Icon.GetApp />
                                </IconButton>
                            </ListSubheader>

                            <Divider />
                        </>
                    }
                    dense
                >
                    <DataIndicators
                        emptyIcon="Notifications"
                        emptyMessage={t('DASHBOARD.NO_EVENTS')}
                        errorMessage={t('DASHBOARD.ERROR_LOADING_DATA')}
                        data={firstChunk}
                        isEmpty={isEmpty}
                        inline
                    />
                    {isDataLoaded(firstChunk) && (
                        <>
                            {visibleEvents.map((timelineDatum, index) => (
                                <TimelineListItem
                                    key={index}
                                    value={timelineDatum}
                                    appRoute={props.eventDetailAppRoute}
                                    isLast={index + 1 === visibleEvents.length}
                                    testId="timeline-list-item"
                                    viewType="small"
                                    options={{ showDeviceName: true, showBadges: true }}
                                />
                            ))}
                        </>
                    )}
                </List>
                {isDataLoaded(firstChunk) && !isEmpty && (
                    <>
                        <Divider />
                        <ActionRow
                            label={t('DASHBOARD.SITE_DETAILS.TIMELINE_CARD.VIEW_ALL_EVENTS')}
                            appRoute={props.viewAllEventsAppRoute}
                        />
                    </>
                )}
            </Card>
        </div>
    );
};
